import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'pacContainer']

  connect() {
    this.inputTarget.placeholder = 'Entrez une ville, un arrondissement...';
    
    this.predictions = [];
    this.selectedIndex = -1;
    this.debounceTimer = null;
    this.hasSearched = false;

    this._handleInput = () => this.handleInput();
    this._handleKeyDown = (e) => this.handleKeyDown(e);
    
    this.inputTarget.addEventListener('input', this._handleInput);
    this.inputTarget.addEventListener('keydown', this._handleKeyDown);
    window.addEventListener('click', this.closePredictions);
  }

  disconnect() {
    this.inputTarget.removeEventListener('input', this._handleInput);
    this.inputTarget.removeEventListener('keydown', this._handleKeyDown);
    window.removeEventListener('click', this.closePredictions);
    clearTimeout(this.debounceTimer);
  }

  handleInput() {
    const value = this.inputTarget.value;
    if (value.length < 3) {
      this.showPredictions([]);
      this.hasSearched = false;
      return;
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.fetchCities(value);
    }, 300);
  }

  async fetchCities(query) {
    try {
      const params = new URLSearchParams({
        'q[name_or_zip_codes_agg_cont_any]': query
      });
      
      const response = await fetch(`/api/v1/autocompletes/zones?${params.toString()}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      this.hasSearched = true;
      this.showPredictions(data.results || []);
    } catch (error) {
      console.error('Error fetching zones:', error);
      this.hasSearched = true;
      this.showPredictions([]);
    }
  }

  handleKeyDown(event) {
    if (!this.predictions.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.predictions.length - 1);
        this.updateHighlight();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.updateHighlight();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.selectedIndex >= 0) {
          this.selectPrediction(this.predictions[this.selectedIndex]);
        }
        break;
      case 'Escape':
        this.showPredictions([]);
        this.hasSearched = false;
        break;
    }
  }

  updateHighlight() {
    const items = this.pacContainerTarget.querySelectorAll('li');
    items.forEach((li, index) => {
      li.classList.toggle('bg-ih-purple-5', index === this.selectedIndex);
    });
  }

  showPredictions(zones) {
    this.predictions = zones;
    this.selectedIndex = -1;
    this.pacContainerTarget.innerHTML = '';

    if (!zones.length) {
      this.showEmptyMessage();
      return;
    }

    const ul = document.createElement('ul');
    ul.className = 'absolute z-50 w-full bg-white rounded-2xl border border-ih-purple-20 overflow-hidden mt-1 top-full left-0';

    zones.forEach((zone) => {
      const li = document.createElement('li');
      
      if (zone.type === 'City') {
        const cityName = document.createElement('div');
        cityName.className = 'font-medium';
        cityName.textContent = zone.name;
        li.appendChild(cityName);
        
        if (zone.zip_code) {
          const zipCode = document.createElement('div');
          zipCode.className = 'text-sm text-ih-indigo-50';
          zipCode.textContent = zone.zip_code;
          li.appendChild(zipCode);
        }
      } else {
        const quarterName = document.createElement('div');
        quarterName.className = 'font-semibold text-[15px]';
        quarterName.textContent = zone.name;
        li.appendChild(quarterName);
        
        const cityInfo = document.createElement('div');
        cityInfo.className = 'text-[13px] text-ih-indigo-50 mt-0.5';
        cityInfo.textContent = `${zone.city_name} ${zone.zip_code}`;
        li.appendChild(cityInfo);
      }

      li.className = 'text-ih-indigo first:rounded-t-2xl last:rounded-b-2xl hover:bg-ih-purple-5 cursor-pointer select-none py-3 px-3 border-b border-b-ih-purple-10';
      li.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectPrediction(zone);
      });
      
      ul.appendChild(li);
    });

    this.pacContainerTarget.appendChild(ul);
  }

  closePredictions = () => {
    this.showPredictions([]);
  }

  showEmptyMessage() {
    if (!this.hasSearched || this.inputTarget.value.length < 4) return;

    const p = document.createElement('p');
    p.className = 'absolute z-50 w-full bg-white rounded-2xl border border-ih-purple-20 mt-1 top-full left-0 py-3 px-3 text-ih-indigo text-center';
    p.textContent = 'Nous ne couvrons pas encore cette zone';
    
    this.pacContainerTarget.appendChild(p);
  }

  selectPrediction = (zone) => {
    this.showPredictions([]);
    
    this.inputTarget.value = zone.display_name;
    this.redirectToListingAds(zone);
  }

  stopPropagation = (e) => {
    e.stopPropagation();
  }

  redirectToListingAds(zone) {
    if (zone) {
      const zoneObject = {
        class: zone.type,
        id: zone.id,
        displayedName: zone.display_name
      };
      const zonesParam = encodeURIComponent(JSON.stringify([zoneObject]));
      window.location = `/annonces?zones=${zonesParam}`;
    } else {
      this.redirectToDefault();
    }
  }

  redirectToDefault = () => {
    window.location = '/annonces';
  }
}
